/**
 * Sprint 23.6 — SubAgentAvatarStack.
 *
 * Q12 — three render modes by sub-agent count, all in one component:
 *
 *   • 0           — caller hides the whole "Manages" line; we render null.
 *   • 1-3         — inline avatar+name list, one per line.
 *   • 4+          — overlapping avatar stack (negative-margin circles) + a
 *                   count badge "+N" + a "Show all N →" expand link.
 *
 * Click an avatar / name → navigate to that agent's profile (Q66). The expand
 * link reveals the full list inline (the spec leaves popover-vs-inline to
 * dogfood; inline is simpler — picks up styling from the existing flow).
 *
 * Avatar stack caps at 20 inline-expanded; 21+ remain in the rest popover
 * style — left as a v1 trim ("inline expand caps at 20" risk row).
 */

import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { NodeAvatar } from "../primitives";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import type { LibraryNode } from "../../../lib/types";

const INLINE_THRESHOLD = 3;
const STACK_SHOWN = 4;
const INLINE_CAP = 20;

export interface SubAgentAvatarStackProps {
  agents: LibraryNode[];
  /** Optional themed label for the "Show all N →" expand link. */
  showAllLabel?: string;
}

export function SubAgentAvatarStack({ agents, showAllLabel = "Show all" }: SubAgentAvatarStackProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  if (agents.length === 0) return null;

  const inline = agents.length <= INLINE_THRESHOLD;
  const sorted = agents
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));

  if (inline) {
    return (
      <ul style={list}>
        {sorted.map((a) => (
          <AgentRow key={a.id} agent={a} onClick={() => navigate(`/agents/${a.id}`)} />
        ))}
      </ul>
    );
  }

  if (!expanded) {
    const shown = sorted.slice(0, STACK_SHOWN);
    const hiddenCount = sorted.length - shown.length;
    return (
      <div style={stackWrap}>
        <div style={stack}>
          {shown.map((a, i) => (
            <button
              key={a.id}
              type="button"
              onClick={() => navigate(`/agents/${a.id}`)}
              style={{ ...stackAvatar, marginLeft: i === 0 ? 0 : -6, zIndex: shown.length - i }}
              title={a.name}
              aria-label={a.name}
            >
              <NodeAvatar
                type="agent"
                persona={getAgentPersona({
                  path: a.path,
                  agent_config: a.agent_config as Record<string, unknown> | null,
                })}
                size="sm"
                name={a.name}
              />
            </button>
          ))}
          {hiddenCount > 0 && <span style={countBadge}>+{hiddenCount}</span>}
        </div>
        <button type="button" style={expandLink} onClick={() => setExpanded(true)}>
          {showAllLabel} {sorted.length} →
        </button>
      </div>
    );
  }

  // Inline-expanded; trim very large lists per spec risk row.
  const inlineList = sorted.slice(0, INLINE_CAP);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <ul style={list}>
        {inlineList.map((a) => (
          <AgentRow key={a.id} agent={a} onClick={() => navigate(`/agents/${a.id}`)} />
        ))}
      </ul>
      {sorted.length > INLINE_CAP && (
        <span style={overflowNote}>
          + {sorted.length - INLINE_CAP} more — open Workforce to browse the full list.
        </span>
      )}
      <button type="button" style={expandLink} onClick={() => setExpanded(false)}>
        Collapse
      </button>
    </div>
  );
}

function AgentRow({ agent, onClick }: { agent: LibraryNode; onClick: () => void }) {
  const persona = getAgentPersona({
    path: agent.path,
    agent_config: agent.agent_config as Record<string, unknown> | null,
  });
  return (
    <li>
      <button type="button" style={row} onClick={onClick}>
        <NodeAvatar type="agent" persona={persona} size="sm" name={agent.name} />
        <span style={rowName}>{agent.name}</span>
      </button>
    </li>
  );
}

const list: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  margin: 0,
  padding: 0,
  listStyle: "none",
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "4px 4px",
  border: "none",
  background: "transparent",
  color: "var(--color-text-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  textAlign: "left",
};

const rowName: CSSProperties = {
  fontSize: 13,
};

const stackWrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const stack: CSSProperties = {
  display: "flex",
  alignItems: "center",
};

const stackAvatar: CSSProperties = {
  display: "inline-flex",
  // 2px ring in the section background colour so the circles overlap cleanly
  // against the bg-secondary sidebar card (per the visual contract).
  border: "2px solid var(--color-background-secondary)",
  borderRadius: "50%",
  background: "transparent",
  padding: 0,
  cursor: "pointer",
};

const countBadge: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: -6,
  minWidth: 22,
  height: 22,
  padding: "0 5px",
  borderRadius: "50%",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  fontSize: 10,
  fontWeight: 500,
  border: "2px solid var(--color-background-secondary)",
  zIndex: 0,
};

const expandLink: CSSProperties = {
  alignSelf: "flex-start",
  fontSize: 11,
  padding: 0,
  border: "none",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const overflowNote: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  fontStyle: "italic",
};
